<div class="image-processing-tool-container">
  <!-- Header Toolbar (matches Python UI exactly) -->
  <div class="toolbar">
    <div class="toolbar-left">
      <span class="section-label">File Upload</span>
      
      <!-- File Upload Buttons (matching Python UI colors) -->
      <button class="btn btn-blue" (click)="browseFiles()">
        Browse Files
      </button>
      <button class="btn btn-teal" (click)="browseFolder()">
        Browse Folder
      </button>
      <button class="btn btn-blue" (click)="toggleLogSection()">
        {{ showLogSection ? 'Hide Logs' : 'Show Logs' }}
      </button>
      <button class="btn btn-orange" (click)="resetAll()">
        Reset All
      </button>
      
      <!-- Pin Level 1 Checkbox -->
      <label class="checkbox-label">
        <input type="checkbox" [(ngModel)]="pinLevel1">
        Pin L1
      </label>
      
      <!-- Add Level Button (like Python's + button) -->
      <button class="btn btn-green" (click)="addLevel()" [disabled]="levels.length >= 6">
        + Level
      </button>
      
      <!-- Keyboard Shortcuts Button -->
      <button class="btn btn-teal" [matMenuTriggerFor]="shortcutsMenu">
        Shortcuts
      </button>
      
      <!-- Shortcuts Menu -->
      <mat-menu #shortcutsMenu="matMenu" class="shortcuts-menu">
        <div class="shortcuts-content" (click)="$event.stopPropagation()">
          <div class="shortcuts-title">Keyboard Shortcuts</div>
          <div class="shortcuts-section">
            <div class="section-header">Navigation</div>
            <div class="shortcut-row"><span class="key">Tab</span> Switch between Nav/Work panes</div>
            <div class="shortcut-row"><span class="key">1-6</span> Focus Level 1-6</div>
            <div class="shortcut-row"><span class="key">←/→</span> Navigate images</div>
          </div>
          <div class="shortcuts-section">
            <div class="section-header">Actions</div>
            <div class="shortcut-row"><span class="key">Ctrl+A</span> Select all in current pane</div>
            <div class="shortcut-row"><span class="key">Space</span> Toggle selection</div>
            <div class="shortcut-row"><span class="key">Delete</span> Delete selected images</div>
            <div class="shortcut-row"><span class="key">J</span> Join selected images</div>
          </div>
          <div class="shortcuts-section">
            <div class="section-header">Line / Cutting</div>
            <div class="shortcut-row"><span class="key">V</span> Toggle vertical line mode</div>
            <div class="shortcut-row"><span class="key">H</span> Toggle horizontal line mode</div>
            <div class="shortcut-row"><span class="key">C</span> Cut at lines</div>
            <div class="shortcut-row"><span class="key">Esc</span> Clear lines</div>
          </div>
        </div>
      </mat-menu>
      
      <!-- Status Indicators (like Python UI) -->
      <span class="status-indicator" [class.connected]="sessionState?.dbEnabled">
        <span class="dot"></span>
        DB: {{ sessionState?.dbEnabled ? 'Connected' : 'Disconnected' }}
      </span>
      <span class="status-indicator" [class.connected]="sessionState?.gcsEnabled">
        <span class="dot"></span>
        GCS: {{ sessionState?.gcsEnabled ? 'Connected' : 'Disconnected' }}
      </span>
    </div>
    
    <div class="toolbar-right">
      <span class="file-status">{{ getTotalImageCount() > 0 ? getTotalImageCount() + ' files loaded' : 'No files loaded' }}</span>
    </div>
  </div>
  
  <!-- Loading Overlay -->
  <div class="loading-overlay" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading...</span>
  </div>
  
  <!-- Main Content Area -->
  <div class="main-content" [class.with-logs]="showLogSection">
    <!-- Levels Container (horizontal scroll) -->
    <div #levelsContainer class="levels-container" [class.pinned]="pinLevel1">
      <!-- Dynamic Levels using *ngFor -->
      <ng-container *ngFor="let level of levels; let i = index; let isLast = last">
        <div 
          class="level-wrapper level-{{level}}"
          [class.level-1]="level === 1"
          [class.pinned]="level === 1 && pinLevel1"
          [style.width.px]="levelWidths[i]"
          [attr.data-level]="level">
          <app-level-pane
            [level]="level"
            [levelState]="getLevelState(level)"
            [navPaneWidth]="getNavPaneWidth(level)"
            [isFocused]="focusedPane.level === level"
            [focusedPaneType]="focusedPane.level === level ? focusedPane.type : null"
            [isLastLevel]="isLast"
            (navPaneResized)="onNavPaneResize(level, $event)"
            (paneFocused)="onPaneFocused(level, $event)"
            (filesDropped)="processFiles(level, $event)"
            (cutRequested)="cutImage(level, $event.imageId, $event.cutLines, $event.cutType)"
            (cropRequested)="cropImage(level, $event.imageId, $event.cropRect)"
            (joinRequested)="joinImages(level, $event)"
            (undoJoinRequested)="undoJoin(level)"
            (moveToNextLevel)="moveToNextLevel(level)"
            (deleteRequested)="deleteSelectedImages(level)"
            (deleteImageRequested)="deleteSingleImage(level, $event)"
            (deleteAllRequested)="hierarchicalDeleteLevel(level)"
            (undoRequested)="undo()"
            (redoRequested)="redo()"
            (saveRequested)="saveLevel(level)"
            (uploadGcsRequested)="uploadToGcs(level)"
            (selectAllChanged)="onSelectAll(level, $event)"
            (openInWindowsViewer)="openImageInWindowsViewer(level, $event)"
            (debugLog)="addDebugLog($event)">
          </app-level-pane>
        </div>
        
        <!-- Resizable separator between levels (like Python ResizeHandle) -->
        <div *ngIf="!isLast" 
             class="level-separator"
             [class.resizing]="isResizingLevel && resizingLevelIndex === i"
             (mousedown)="startLevelResize($event, i)"
             matTooltip="Drag to resize level width">
        </div>
      </ng-container>
    </div>
    
    <!-- Log Section (below levels - like Python UI) -->
    <div class="log-section" *ngIf="showLogSection">
      <div class="log-header">
        <span class="log-title">Logs ({{ debugLogs.length }})</span>
        <div class="log-actions">
          <button mat-icon-button (click)="copyLogsToClipboard()" matTooltip="Copy to Clipboard">
            <mat-icon>content_copy</mat-icon>
          </button>
          <button mat-icon-button (click)="clearDebugLogs()" matTooltip="Clear Logs">
            <mat-icon>delete_sweep</mat-icon>
          </button>
          <button mat-icon-button (click)="toggleLogSection()" matTooltip="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
      <div class="log-content">
        <div class="log-entry" *ngFor="let log of debugLogs">{{ log }}</div>
        <div class="log-empty" *ngIf="debugLogs.length === 0">No logs yet</div>
      </div>
    </div>
  </div>
</div>